import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { CustomerContact, ID } from '@shared/types';
import { buildBaseFields, buildUpdateFields, fromSnapshot } from '@shared/utils';
import { writeAuditLog } from '@shared/utils';

const contactCreateSchema = z.object({
  name: z.string().min(1, '氏名は必須です'),
  nameKana: z.string().min(1, 'フリガナは必須です'),
  department: z.string().nullable().default(null),
  position: z.string().nullable().default(null),
  phone: z.string().nullable().default(null),
  mobile: z.string().nullable().default(null),
  email: z.string().email().nullable().default(null),
  isPrimary: z.boolean().default(false),
});

export type ContactCreateInput = z.infer<typeof contactCreateSchema>;
export type ContactUpdateInput = Partial<ContactCreateInput>;

function contactsCol(customerId: ID) {
  return collection(db, 'customers', customerId, 'contacts');
}

export async function fetchContacts(customerId: ID): Promise<CustomerContact[]> {
  const q = query(contactsCol(customerId), where('isDeleted', '==', false), orderBy('name'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as CustomerContact);
}

export async function fetchContact(customerId: ID, contactId: ID): Promise<CustomerContact | null> {
  const snap = await getDoc(doc(contactsCol(customerId), contactId));
  return fromSnapshot<CustomerContact>(snap);
}

export async function createContact(
  customerId: ID,
  data: ContactCreateInput,
  userId: ID
): Promise<CustomerContact> {
  const validated = parseOrThrow(contactCreateSchema, data);
  const payload = { ...validated, customerId, ...buildBaseFields(userId) };
  const ref = await addDoc(contactsCol(customerId), payload);
  await writeAuditLog({ entityType: 'customer_contact', entityId: ref.id, action: 'create', userId, after: validated });
  return { id: ref.id, ...payload } as unknown as CustomerContact;
}

export async function updateContact(
  customerId: ID,
  contactId: ID,
  data: ContactUpdateInput,
  userId: ID
): Promise<CustomerContact> {
  const ref = doc(contactsCol(customerId), contactId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('担当者が見つかりません。');
  const before = snap.data();
  const patch = { ...data, ...buildUpdateFields(userId) };
  await updateDoc(ref, patch);
  await writeAuditLog({ entityType: 'customer_contact', entityId: contactId, action: 'update', userId, before, after: data });
  return { id: contactId, ...before, ...patch } as unknown as CustomerContact;
}

export async function deleteContact(customerId: ID, contactId: ID, userId: ID): Promise<void> {
  const ref = doc(contactsCol(customerId), contactId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('担当者が見つかりません。');
  await updateDoc(ref, { isDeleted: true, ...buildUpdateFields(userId) });
  await writeAuditLog({ entityType: 'customer_contact', entityId: contactId, action: 'delete', userId, before: snap.data() });
}
